import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import baseUrl from '../../base_url';
import Sidebar from './components/sidebar';

const Returns = () => {
  const navigate = useNavigate();
  
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Requested');
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [returnsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  
  // Check if admin is logged in
  useEffect(() => {
    const getUser = async() => {
      try {
        await axios.get(`${baseUrl}api/admin/checkLogin`, { withCredentials: true });
      } catch(err) {
        console.log("error while checking if logged in");
        console.log(err);
        navigate("/admin/login");
      }
    };
    getUser();
  }, [navigate]);
  
  // Function to fetch returns
  const fetchReturns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}api/admin/returns`, { withCredentials: true });
      
      if (response?.data?.data && response?.data?.data?.length > 0) {
        setReturns(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching returns:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Initial data fetch
  useEffect(() => {
    fetchReturns();
  }, []);
  
  // Handle refresh click
  const handleRefresh = () => {
    setRefreshing(true);
    fetchReturns();
  };
  
  // Format date from ISO string
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${day}, ${year} ${date.toLocaleTimeString('en-US', { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true 
    })}`;
  };
  
  // Filter returns based on active tab and search query
  const filteredReturns = returns.filter(returnItem => {
    if (activeTab === 'All') return true;
    if (activeTab === 'Requested') return returnItem.status === 'Requested';
    if (activeTab === 'Approved') return returnItem.status === 'Approved';
    if (activeTab === 'Refunded') return returnItem.status === 'Refunded';
    if (activeTab === 'Picked Up') return returnItem.status === 'Picked Up';
    return true;
  }).filter(returnItem => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    
    // Search by return ID
    const returnId = returnItem._id ? returnItem._id.toLowerCase() : '';
    
    // Search by order ID
    const orderId = returnItem.orderId && returnItem.orderId._id 
      ? returnItem.orderId._id.toLowerCase() 
      : '';
    
    // Search by customer name (if available in address)
    const customerName = returnItem.orderId && 
                         returnItem.orderId.address && 
                         returnItem.orderId.address.length > 0 
      ? returnItem.orderId.address[0].fullName.toLowerCase() 
      : '';
    
    // Search by product name
    const productName = returnItem.productId && returnItem.productId.title 
      ? returnItem.productId.title.toLowerCase() 
      : '';
    
    return returnId.includes(searchLower) || 
           orderId.includes(searchLower) || 
           customerName.includes(searchLower) ||
           productName.includes(searchLower);
  });
  
  // Calculate pagination
  useEffect(() => {
    setTotalPages(Math.ceil(filteredReturns.length / returnsPerPage));
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [filteredReturns.length, returnsPerPage, activeTab, searchQuery]);
  
  // Get current returns for the page
  const indexOfLastReturn = currentPage * returnsPerPage;
  const indexOfFirstReturn = indexOfLastReturn - returnsPerPage;
  const currentReturns = filteredReturns.slice(indexOfFirstReturn, indexOfLastReturn);
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Previous page
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Next page
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const ellipsis = <span key="ellipsis" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
    
    if (totalPages <= 5) {
      // If total pages is 5 or less, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);
      
      if (currentPage <= 3) {
        // If current page is among the first 3 pages
        pageNumbers.push(2, 3, 4);
        pageNumbers.push('ellipsis');
      } else if (currentPage >= totalPages - 2) {
        // If current page is among the last 3 pages
        pageNumbers.push('ellipsis');
        pageNumbers.push(totalPages - 3, totalPages - 2, totalPages - 1);
      } else {
        // Current page is in the middle
        pageNumbers.push('ellipsis');
        pageNumbers.push(currentPage - 1, currentPage, currentPage + 1);
        pageNumbers.push('ellipsis2');
      }
      
      // Always show last page
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers.map((number, index) => {
      if (number === 'ellipsis' || number === 'ellipsis2') {
        return ellipsis;
      }
      
      return (
        <button
          key={`page-${number}-${index}`}
          onClick={() => paginate(number)}
          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
            currentPage === number
              ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          {number}
        </button>
      );
    });
  };
  
  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'Requested':
        return 'bg-yellow-500 text-white';
      case 'Approved':
        return 'bg-blue-500 text-white';
      case 'Refunded':
        return 'bg-green-600 text-white';
      case 'Picked Up':
        return 'bg-purple-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };
  
  // Generate a short ID
  const getShortId = (id) => {
    return id ? `#${id.substring(id.length - 6)}` : 'N/A';
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Use the separate Sidebar component */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6 bg-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-700">Returns</h1>
            <button 
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-blue-400"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className={`h-5 w-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div className="bg-white rounded-md shadow">
            {/* Tabs */}
            <div className="flex border-b overflow-x-auto">
              {['Requested', 'Approved', 'Refunded', 'Picked Up', 'All'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-6 ${activeTab === tab ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Search & Filter */}
            <div className="flex p-4 border-b">
              <div className="relative flex-1">
                <div className="flex">
                  <div className="w-1/4 mr-4">
                    <button className="w-full px-4 py-2 bg-gray-100 text-gray-600 rounded-md flex items-center justify-between">
                      <span>Filter</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  <div className="w-3/4 relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </span>
                    <input 
                      className="block w-full pl-10 pr-3 py-2 border rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500" 
                      placeholder="Search returns by ID, order, product or customer" 
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="flex flex-col items-center">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-10 w-10 text-blue-500 animate-spin mb-2" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <div className="text-lg text-gray-500">Loading returns...</div>
                </div>
              </div>
            ) : (
              <>
                {/* Returns Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Return ID</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested On</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentReturns.length > 0 ? (
                        currentReturns.map((returnItem) => {
                          // Extract customer name from order data
                          const customerName = returnItem.orderId && 
                                              returnItem.orderId.address && 
                                              returnItem.orderId.address.length > 0 
                            ? returnItem.orderId.address[0].fullName 
                            : 'N/A';
                          
                          // Get product title
                          const productTitle = returnItem.productId && returnItem.productId.title
                            ? returnItem.productId.title.substring(0, 30) + (returnItem.productId.title.length > 30 ? '...' : '')
                            : 'N/A';
                          
                          return (
                            <tr key={returnItem._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <a href={`/admin/return/${returnItem._id}`} className="text-sm font-medium text-blue-600">
                                  {getShortId(returnItem._id)}
                                </a>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <a href={`/admin/order/${returnItem.orderId?._id}`} className="text-sm font-medium text-blue-600">
                                  {getShortId(returnItem.orderId?._id)}
                                </a>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {productTitle}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {customerName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium flex items-center justify-center w-[95px] ${getStatusBadgeClass(returnItem.status)}`}>
                                  {returnItem.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {returnItem.reason || 'Not specified'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(returnItem.requestedAt || returnItem.createdAt)}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                            No returns found matching your criteria
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button 
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === 1 ? 'text-gray-400 bg-gray-100' : 'text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      Previous
                    </button>
                    <button 
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === totalPages ? 'text-gray-400 bg-gray-100' : 'text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{filteredReturns.length > 0 ? indexOfFirstReturn + 1 : 0}</span> to{' '}
                        <span className="font-medium">{Math.min(indexOfLastReturn, filteredReturns.length)}</span> of{' '}
                        <span className="font-medium">{filteredReturns.length}</span> results
                      </p>
                    </div>
                    {totalPages > 1 && (
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button 
                            onClick={goToPreviousPage}
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 ${
                              currentPage === 1 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            <span className="sr-only">Previous</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                          
                          {getPageNumbers()}
                          
                          <button 
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 ${
                              currentPage === totalPages ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            <span className="sr-only">Next</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Returns;